/* eslint-disable @typescript-eslint/no-explicit-any */
import { isEqual } from 'lodash';
import { KeyValue } from 'ngrx-forms';
import { NgxPermissionsService } from 'ngx-permissions';
import { firstValueFrom, map, takeUntil, withLatestFrom } from 'rxjs';

/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideGetRowsParams,
} from '@ag-grid-community/core';
import { Component, Inject, OnInit } from '@angular/core';
import {
  ConfirmServiceInterface,
  LazyEntityFacadeInterface,
  LoaderFacadeInterface,
  mapFromColumnState,
  mapFromFilterModel,
  RouterFacadeInterface,
  SharedEntityFacadeInterface,
} from '@ideapa/app-common';
import { BaseDto } from '@ideapa/common-dto';
import { FilterValue } from '@ideapa/common-interfaces';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap/modal';

import { BaseListContainer } from './base-list.container';
import { loadPage } from './base-paged-list-action';
import { BaseApiClient } from './base.api-client';

@Component({
  standalone: false,
  template: '',
})
export abstract class BasePagedListContainer<
    T extends BaseDto,
    K extends KeyValue
  >
  extends BaseListContainer<T, K>
  implements OnInit
{
  searchFilters: FilterValue[] = [];

  constructor(
    @Inject('SharedEntityFacade')
    sharedFacade: SharedEntityFacadeInterface<T>,
    @Inject('EntityFacade')
    facade: LazyEntityFacadeInterface<T, K>,
    @Inject('RouterFacade')
    routerFacade: RouterFacadeInterface,
    permissionsService: NgxPermissionsService,
    protected apiClient: BaseApiClient<T>,
    @Inject('LoaderFacade')
    protected loaderFacade: LoaderFacadeInterface,
    @Inject(NgbModal) modalService: NgbModal,
    @Inject('ConfirmService')
    protected confirm: ConfirmServiceInterface
  ) {
    super(
      sharedFacade,
      facade,
      routerFacade,
      permissionsService,
      modalService,
      confirm
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.gridOptions$ = this.columnDefs$.pipe(
      withLatestFrom(this.pageSize$),
      map(([columnDefs, pageSize]) => {
        // this.columnDefs = columnDefs;

        const customGridOptions = {
          pagination: true,
          rowModelType: 'serverSide',
          // serverSideStoreType: 'partial' (v28) → serverSideInfiniteScroll (v29-30) → default in v32
          cacheBlockSize: pageSize,
          paginationPageSize: pageSize,
          onGridReady: (e: GridReadyEvent) => this.onGridReady(e),
        } as GridOptions;
        return Object.assign(this.commonGridOptions, customGridOptions);
      })
    );
  }

  override onGridReady(event: GridReadyEvent) {
    super.onGridReady(event);

    this.pageSize$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((pageSize) => {
      setTimeout(() => {
        this.setDatasource(event.api, pageSize);
      }, 0);
    });
  }

  protected setDatasource(api: GridApi, pageSize: number) {
    api.setGridOption('serverSideDatasource', {
      destroy: () => {
        // console.log('destroy datasource');
      },
      getRows: async (params: IServerSideGetRowsParams) => {
        // Anti redux pattern
        try {
          this.loaderFacade.showLoader(loadPage);

          this.facade.setPageLoaded(false);

          const presetFilters = await firstValueFrom(this.presetFilters$);
          const fields = await firstValueFrom(this.fields$);

          // TODO ANDREA: sistemare filtri di tipo datetime
          const filters = mapFromFilterModel(
            params.request.filterModel as any,
            fields
          );

          this.searchFilters.forEach((sf) => {
            const index = filters.findIndex((f) => f.field === sf.field);
            if (index >= 0) {
              filters[index] = sf;
            } else {
              filters.push(sf);
            }
          });

          presetFilters.forEach((sf) => {
            const index = filters.findIndex(
              (f) =>
                f.field === sf.field &&
                f.type === sf.type &&
                f.operator === sf.operator &&
                isEqual(f.value, sf.value)
            );
            if (index >= 0) {
              filters[index] = sf;
            } else {
              filters.push(sf);
            }
          });

          const areDomainUserFiltersDisabled = await firstValueFrom(
            this.areDomainUserFiltersDisabled$
          );

          const isEntitySelectionLimited = await firstValueFrom(
            this.isEntitySelectionLimited$
          );

          const areEnteAnnoFiltersDisabled = await firstValueFrom(
            this.areEnteAnnoFiltersDisabled$
          );

          const orderBy = mapFromColumnState(params.request.sortModel);

          const result = await this.getPaged(
            pageSize,
            params,
            orderBy,
            filters,
            areDomainUserFiltersDisabled,
            isEntitySelectionLimited,
            areEnteAnnoFiltersDisabled
          );

          params.success({
            rowData: result.items,
            rowCount: result.totalCount,
          });

          this.sharedFacade.loadPagedSuccess(result.items);

          this.facade.setTotalRows(result.totalCount);
          this.facade.setPageLoaded(true);
          this.loaderFacade.hideLoader(loadPage);
        } catch (err) {
          this.loaderFacade.hideLoader(loadPage);
          this.sharedFacade.loadPagedFailure(err as any);
        }
      },
    });
  }

  protected async getPaged(
    pageSize: number,
    params: IServerSideGetRowsParams<any>,
    orderBy: { [field: string]: 'asc' | 'desc' },
    filters: FilterValue[],
    areDomainUserFiltersDisabled: boolean,
    isEntitySelectionLimited: boolean,
    areEnteAnnoFiltersDisabled: boolean
  ) {
    const result = await firstValueFrom(
      this.apiClient.getPaged(
        pageSize,
        params.request.startRow || 0,
        orderBy,
        filters,
        this.join,
        areDomainUserFiltersDisabled,
        isEntitySelectionLimited,
        areEnteAnnoFiltersDisabled
      )
    );

    return result;
  }
}
